import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import useCheckUserClubRole from '../../../hooks/useCheckUserClubRole';
import useGetCurrentClub from '../../../hooks/useGetCurrentClub';
import { useHomeGamesWidget } from '../../../model';
import { UserClubRole } from '../../../types/common';
import NoData from '../../NoData/NoData';
import ShareButton from '../Header/ShareButton/ShareButton';

import {
  TXTCLI_Post_new_message_to_the_club_w_ELL,
  TXTWCL_LetXs_start,
  TXTWCL_Welcome_to_N0
} from './Wall.messages.json';
import EmptyWall from './assets/empty-wall.svg';
import Reply from './components/Reply';
import type { ReplyProps } from './components/WallPost';
import WallPosts from './components/WallPosts';
import useClubWallRequest from './hooks/useClubWallRequest';
import styles from './shared.scss';

const NoResults: React.FC<React.PropsWithChildren<{ clubName: string }>> = ({
  clubName,
  children
}) => (
  <NoData
    icon={<EmptyWall />}
    primaryText={<Translation {...TXTWCL_Welcome_to_N0} values={{ 0: clubName }} />}
    secondaryText={<Translation {...TXTWCL_LetXs_start} />}
  >
    {children}
  </NoData>
);

const Wall: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { widgetSelect } = useHomeGamesWidget();

  const posts = useSelector(widgetSelect.wallPosts);

  const { clubId } = useParams<{ clubId: string }>();
  const club = useGetCurrentClub(clubId);
  const memberRole = club?.memberRole ?? undefined;
  const { handleSubmit } = useClubWallRequest({ clubId, setLoading, roleFlags: memberRole });
  const { formatMessage } = useIntl();
  const isManager = useCheckUserClubRole(clubId, [UserClubRole.Manager]);
  const canShare = useCheckUserClubRole(clubId, [UserClubRole.Manager, UserClubRole.Admin]);

  const handleCreateWallMessage = (post: string) => {
    handleSubmit({
      post,
      obsolete: false
    });
  };

  const handleReply = ({ post, parentId }: ReplyProps) => {
    handleSubmit({
      post,
      obsolete: false,
      parentId
    });
  };

  const handleDeleteWallPost = (id: number) => {
    handleSubmit({
      obsolete: true, // required for delete
      id
    });
  };

  const replyPlaceholder = formatMessage(TXTCLI_Post_new_message_to_the_club_w_ELL);

  return (
    <div className={classNames(styles.container)}>
      {posts.length ? (
        <WallPosts
          loading={loading}
          isManager={isManager}
          onDeleteWallPost={handleDeleteWallPost}
          onReply={handleReply}
          posts={posts}
        />
      ) : (
        <NoResults clubName={club.clubName || ''}>{canShare && <ShareButton />}</NoResults>
      )}

      <Reply
        loading={loading}
        placeholder={replyPlaceholder}
        handleSubmit={handleCreateWallMessage}
        containerClassName={styles.replyThread}
        textClassName={classNames(styles.replyMessage, styles.textBorder)}
        buttonClassName={styles.threadBtn}
        useSeparator
      />
    </div>
  );
};

export default Wall;
